import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Internal Components */
import { createSocketData, readSocketData, deleteSocketData, handleProcessRowUpdate } from 'shared/lib/fetchSocket';
import { createFillDownHandler } from 'shared/lib/datagridHelpers';
import { useShipmentTodos } from '../../lib/useShipmentTodos';
import useInvalidOptionWarning from 'shared/hooks/useInvalidOptionWarning';
import SingleDatagrid from 'shared/ui/Datagrid';
import ThemeButton from 'shared/ui/Button';
import { CustomTextPopover } from 'shared/ui/Popover';

import { notAllowNullKey, dateField } from 'data/shipmentDetailConfig';
import LinkDialog from './LinkDialog';
import { bolCol, bolGroupingModel } from '../TableDefinition/TruckingSubDef';

export default function UpdateTrucking(props) {
    /** Get props from ShipmentDetail.js */
    const { row, presetData, tabConfig } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State to manage an autocomplete options useing websocket(Volume Unit). */
    const [unitOptions, setUnitOptions] = useState([]);
    /** State to manage an autocomplete options useing websocket(Trucker). */
    const [tradePartnerOptions, setTradePartnerOptions] = useState([]);
    /** State to manage link dialog. */
    const [linkOpen, setLinkOpen] = useState(false);
    /** State for a selected single container data when opening the link dialog. */
    const [cntrRow, setCntrRow] = useState({});

    /** Check if case_closure is Complete */
    const isCaseClosureComplete = row?.case_closure === 'Complete';

    /** Helper to make all columns non-editable when case is closed. */
    const disableColumnsIfComplete = (cols) => {
        if (!isCaseClosureComplete) return cols;
        return cols.map((col) => ({ ...col, editable: false }));
    };
    const [currentPresetNote, setCurrentPresetNote] = useState([]);
    /** Check if the popover is open. */
    const [popoverState, setPopoverState] = useState({
        anchorEls: null, // key: item.id, value: anchorEl
        openIds: null, // key: item.id, value: true/false
    });

    /** All history list of value changes. */
    const historyData = useSelector((state) => state.historyList);
    /** Shipment type value (0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Matching trucking shipment list based on the main data's ID value. */
    const bolData = useSelector((state) => state.truckingBolUpdateForm).filter((item) => item.il_ftl_ltl_id === row.id);
    /** To-do list for the currently selected shipment. */
    const todoList = useShipmentTodos(tabValue, row.id);
    /** Note value of the preset applied to this shipment. */
    const presetNote = useSelector((state) => state.truckingPresetUpdateForm);

    /** When mainRow or preset data changes, update the currently selected preset notes. */
    useEffect(() => {
        const presetId = row?.il_opus_reference_preset?.il_preset_id;
        if (presetId) {
            const note = presetNote.find((item) => item.id === presetId)?.il_preset_table_note;
            setCurrentPresetNote(note || []);
        }
    }, [presetNote, row]);

    /** Handler for emitting to the server to add a new alias row. */
    const handleAddBOL = (newValue) => {
        if (isCaseClosureComplete) return;
        const newBOL = { ...tabConfig.newBOL, id: randomId(), il_ftl_ltl_id: newValue.id };
        createSocketData(dispatch, 4, 'row', 'il_ftl_ltl_alias', 'addTruckingBOL', newBOL);
    };

    /**
     * Handle to delete a alias row.
     * Ensure at least one alias remains before delete.
     *
     * @param {string} id - The ID of the container to delete.
     */
    const handleDeleteClick = (id) => {
        if (isCaseClosureComplete) return;
        if (bolData.length > 1) {
            if (window.confirm('Are you sure you want to delete the entry?')) {
                deleteSocketData(dispatch, 4, 'row', 'il_ftl_ltl_alias', 'deleteBOL', id);
            }
        } else {
            window.alert('At least one alias must be provided. ');
        }
    };

    /** Handler for updating a selected contaiiner data and link dialog. */
    const handleLinkClick = (row) => {
        setCntrRow(row);
        setLinkOpen(true);
    };

    /** Handle to change autocomplete opus unit list and call Websocket to receive the searched result. */
    const handleUnitChange = (event, value) => {
        readSocketData(dispatch, 4, 'cell', 'il_opus_volume_unit', '', value, setUnitOptions);
    };

    /** Handle to change autocomplete trucker name and call Websocket to receive the searched result. */
    const handleTradePartnerChange = (event, value, params) => {
        const type = params.field;
        readSocketData(dispatch, 0, 'cell', 'il_trade_partner', type, value, setTradePartnerOptions);
    };

    /** Prefetch unit/trucker options as soon as the cell enters edit mode, before the user types. */
    const handleCellEditStart = (params) => {
        if (params.field === 'il_opus_volume_unit_id') {
            handleUnitChange(null, params.value || '');
        } else if (params.field === 'il_trade_partner_id') {
            handleTradePartnerChange(null, params.value?.name ?? '', params);
        }
    };

    /** Warn the user when a pasted value is not one of the allowed select options. */
    const handleInvalidOption = useInvalidOptionWarning();

    /** Copy the first BOL row's value in a column to every row below it. */
    const handleFillDown = createFillDownHandler({
        dispatch,
        disabled: isCaseClosureComplete,
        rows: bolData,
        tabValue: 4,
        methodTableName: 'il_ftl_ltl_alias',
        reducerName: 'setBolUpdateData',
        getTableId: (row) => [row.il_ftl_ltl_id],
        formData: bolData,
        notAllowNullKey,
        dateField,
    });

    /** Handle to set the anchor element for the memo popover. */
    const handleMemoClick = (event, id) => {
        setPopoverState({ openIds: id, anchorEls: event.currentTarget });
    };

    /** Handle to close a memo popover. */
    const handleMemoClose = () => {
        setPopoverState({ openIds: null, anchorEls: null });
    };

    return (
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', width: '100%', py: 1 }}
            >
                {tabConfig.preset.map((item, idx) => {
                    const matchedPreset = currentPresetNote?.find((el) => el.attribute == item.attribute);
                    return (
                        <CustomTextPopover
                            key={item.id}
                            open={popoverState.openIds === item.id}
                            anchorEl={popoverState.openIds === item.id ? popoverState.anchorEls : null}
                            handleClick={(e) => handleMemoClick(e, item.id)}
                            handleClose={handleMemoClose}
                            content={matchedPreset ? matchedPreset.value : null}
                            title={item.label}
                            buttonStyle={{ bgcolor: matchedPreset ? '#FFC300' : '#dfdfdf' }}
                        />
                    );
                })}
                <ThemeButton
                    text={'NEW'}
                    icon={<AddIcon />}
                    sx={{ width: '68px' }}
                    onClick={() => handleAddBOL(row)}
                    disabled={isCaseClosureComplete}
                />
            </Box>
            <SingleDatagrid
                rows={bolData}
                cols={disableColumnsIfComplete(
                    bolCol(
                        handleDeleteClick,
                        presetData,
                        unitOptions,
                        handleUnitChange,
                        tradePartnerOptions,
                        handleTradePartnerChange,
                        todoList,
                        handleLinkClick,
                        handleFillDown,
                        handleInvalidOption
                    )
                )}
                columnGroupingModel={bolGroupingModel(presetData, tabValue, historyData, bolData)}
                cellSelection={true}
                additional={{ onCellEditStart: handleCellEditStart }}
                sx={{ height: tabConfig.height, minHeight: tabConfig.minHeight }}
                processRowUpdate={(newRow) => {
                    if (isCaseClosureComplete)
                        return Promise.resolve(bolData.find((r) => r.id === newRow.id) || newRow);
                    return handleProcessRowUpdate({
                        dispatch,
                        tabValue: 4,
                        methodTableName: 'il_ftl_ltl_alias',
                        reducerName: 'setBolUpdateData',
                        tableId: [newRow.il_ftl_ltl_id],
                        formData: bolData,
                        newRow,
                        notAllowNullKey,
                        dateField,
                    });
                }}
            />
            <LinkDialog
                linkOpen={linkOpen}
                setLinkOpen={setLinkOpen}
                cntrRow={cntrRow}
                cntrData={bolData}
                presetData={presetData}
                matchedTodoContainer={todoList}
                containerData={bolData}
                tabConfig={''}
                mainRow={row}
            />
        </Box>
    );
}
